import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useCallback } from 'react'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { Link } from '@tanstack/react-router'
import { deckImportSchema, convertSongToFlashcards } from '@/lib/flashcardSchemas'

export const Route = createFileRoute('/app/upload')({
  component: UploadPage,
})

function UploadPage() {
  const currentUser = useCurrentUser()

  if (currentUser === undefined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading...</div>
      </div>
    )
  }

  if (currentUser === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-trail-tan text-xl font-mono">Please sign in to upload files</div>
      </div>
    )
  }

  return <UploadContent />
}

function UploadContent() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [fileType, setFileType] = useState<'flashcard-deck' | 'song'>('flashcard-deck')
  const [preview, setPreview] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setSelectedFile(file)
    setError(null)

    try {
      const text = await file.text()
      const json = JSON.parse(text)

      if (fileType === 'flashcard-deck') {
        // Try to parse as deck import schema
        try {
          const parsed = deckImportSchema.parse(json)
          setPreview({
            type: 'deck',
            name: parsed.name,
            description: parsed.description,
            flashcardCount: parsed.flashcards.length,
            flashcards: parsed.flashcards.slice(0, 5), // Preview first 5
          })
        } catch (err) {
          // Try to parse as song structure
          const songs = Array.isArray(json) ? json : [json]
          const flashcards = songs.flatMap((song: {
            title: string
            sections: Array<{ type: string; spanish: string; english: string }>
            targetVerb?: string
          }) => convertSongToFlashcards(song))
          setPreview({
            type: 'song-converted',
            flashcardCount: flashcards.length,
            flashcards: flashcards.slice(0, 5),
          })
        }
      } else {
        // Song format
        setPreview({
          type: 'song',
          songs: Array.isArray(json) ? json.slice(0, 5) : [json],
          totalCount: Array.isArray(json) ? json.length : 1,
        })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid JSON file')
      setPreview(null)
    }
  }, [fileType])

  return (
    <div className="min-h-screen bg-black text-white py-4 sm:py-8 px-2 sm:px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-4 sm:mb-8 terminal-border p-4 sm:p-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-sky-blue mb-2 font-mono tracking-wider">
            [JSON UPLOAD]
          </h1>
          <p className="text-trail-tan font-mono text-sm sm:text-base">
            Upload JSON files to create flashcard decks or import songs
          </p>
        </div>

        {/* File Type Selection */}
        <div className="mb-6 terminal-border p-4 bg-black">
          <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
            ► FILE TYPE
          </label>
          <div className="flex gap-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                value="flashcard-deck"
                checked={fileType === 'flashcard-deck'}
                onChange={(e) => {
                  setFileType(e.target.value as 'flashcard-deck')
                  setSelectedFile(null)
                  setPreview(null)
                }}
                className="mr-2"
              />
              <span className="text-trail-tan font-mono">Flashcard Deck</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                value="song"
                checked={fileType === 'song'}
                onChange={(e) => {
                  setFileType(e.target.value as 'song')
                  setSelectedFile(null)
                  setPreview(null)
                }}
                className="mr-2"
              />
              <span className="text-trail-tan font-mono">Song Format</span>
            </label>
          </div>
        </div>

        {/* File Upload */}
        <div className="mb-6 terminal-border p-4 bg-black">
          <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
            ► SELECT JSON FILE
          </label>
          <input
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none file:mr-4 file:py-2 file:px-4 file:bg-terminal-green file:text-black file:border-0 file:font-bold file:font-mono file:cursor-pointer hover:file:bg-terminal-green/80"
          />
          {selectedFile && (
            <p className="text-trail-tan text-xs font-mono mt-2">
              Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
            </p>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-900 border-2 border-red-500 rounded-lg p-4">
            <p className="text-red-200 font-mono text-sm">{error}</p>
          </div>
        )}

        {/* Preview */}
        {preview && (
          <div className="mb-6 terminal-border p-4 bg-black border-terminal-green">
            <h2 className="text-xl font-bold text-terminal-green mb-4 font-mono">
              [PREVIEW]
            </h2>
            {preview.type === 'deck' && (
              <div className="space-y-2 text-sm font-mono">
                <div>
                  <span className="text-sky-blue">Deck Name:</span>{' '}
                  <span className="text-white">{preview.name}</span>
                </div>
                {preview.description && (
                  <div>
                    <span className="text-sky-blue">Description:</span>{' '}
                    <span className="text-white">{preview.description}</span>
                  </div>
                )}
                <div>
                  <span className="text-sky-blue">Flashcards:</span>{' '}
                  <span className="text-terminal-green">{preview.flashcardCount}</span>
                </div>
                <div className="mt-4 text-xs">
                  <div className="text-sky-blue mb-2">Sample flashcards:</div>
                  {preview.flashcards.map((card: any, idx: number) => (
                    <div key={idx} className="mb-2 text-trail-tan">
                      <div>ES: {card.frontSpanish.join(' | ')}</div>
                      <div className="text-gray-400">EN: {card.backEnglish.join(' | ')}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {preview.type === 'song-converted' && (
              <div className="space-y-2 text-sm font-mono">
                <div>
                  <span className="text-sky-blue">Converted to:</span>{' '}
                  <span className="text-terminal-green">{preview.flashcardCount} flashcards</span>
                </div>
                <div className="mt-4 text-xs">
                  <div className="text-sky-blue mb-2">Sample flashcards:</div>
                  {preview.flashcards.map((card: any, idx: number) => (
                    <div key={idx} className="mb-2 text-trail-tan">
                      <div>ES: {card.frontSpanish.join(' | ')}</div>
                      <div className="text-gray-400">EN: {card.backEnglish.join(' | ')}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {preview.type === 'song' && (
              <div className="space-y-2 text-sm font-mono">
                <div>
                  <span className="text-sky-blue">Songs:</span>{' '}
                  <span className="text-terminal-green">{preview.totalCount}</span>
                </div>
                <div className="mt-4 text-xs">
                  <div className="text-sky-blue mb-2">Sample songs:</div>
                  {preview.songs.map((song: any, idx: number) => (
                    <div key={idx} className="mb-2 text-trail-tan">
                      <div className="text-white font-bold">{song.title}</div>
                      <div className="text-gray-400">
                        {song.sections?.length || 0} sections
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        {preview && (
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/app/decks/add"
              className="flex-1 px-6 py-3 bg-terminal-green text-black font-bold font-mono text-lg border-4 border-terminal-green hover:bg-black hover:text-terminal-green transition-all retro-glow text-center"
            >
              CREATE DECK FROM FILE
            </Link>
            <button
              onClick={() => {
                setSelectedFile(null)
                setPreview(null)
                setError(null)
              }}
              className="flex-1 px-6 py-3 bg-black text-trail-tan font-bold font-mono border-4 border-trail-tan hover:bg-trail-tan hover:text-black transition-all"
            >
              CLEAR
            </button>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-8 terminal-border p-4 bg-black">
          <h2 className="text-xl font-bold text-terminal-green mb-4 font-mono">
            [INSTRUCTIONS]
          </h2>
          <div className="space-y-3 text-sm font-mono text-trail-tan">
            <div>
              <span className="text-sky-blue">► Flashcard Deck Format:</span>
              <pre className="mt-2 p-3 bg-black border border-sky-blue text-xs overflow-x-auto">
{`{
  "name": "My Deck",
  "description": "Optional description",
  "flashcards": [
    {
      "frontSpanish": ["hola"],
      "backEnglish": ["hello"],
      "cardType": "word"
    }
  ]
}`}
              </pre>
            </div>
            <div>
              <span className="text-sky-blue">► Song Format:</span>
              <pre className="mt-2 p-3 bg-black border border-sky-blue text-xs overflow-x-auto">
{`[
  {
    "title": "Song Title",
    "sections": [
      {
        "type": "verse",
        "spanish": "Spanish lyrics",
        "english": "English translation"
      }
    ]
  }
]`}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

